import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Wallet, 
  Plus, 
  Minus, 
  DollarSign,
  Clock,
  User,
  FileText,
  Printer,
  History,
  X,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Pencil,
  Trash2,
  Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CashRegisterContent = () => {
  // Estados principais
  const [currentCash, setCurrentCash] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Estados dos modais
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showOperatorsModal, setShowOperatorsModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRegister, setSelectedRegister] = useState(null);
  
  // Estados dos formulários
  const [movementType, setMovementType] = useState('sangria');
  const [openForm, setOpenForm] = useState({ operator_id: '', initial_amount: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ actual_amount: '', notes: '' });
  const [movementForm, setMovementForm] = useState({ amount: '', reason: '' });
  const [newOperator, setNewOperator] = useState('');
  const [saving, setSaving] = useState(false);

  const printRef = useRef();

  useEffect(() => {
    fetchCurrentCash();
    fetchOperators();
  }, []);

  const fetchCurrentCash = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentCash(response.data);
      setIsOpen(response.data.is_open);
    } catch (error) {
      console.error('Error fetching cash:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash/operators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash/history?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleOpenCash = async (e) => {
    e.preventDefault();
    if (!openForm.operator_id) {
      toast.error('Selecione um operador');
      return;
    }
    if (!openForm.initial_amount || parseFloat(openForm.initial_amount) < 0) {
      toast.error('Informe o valor do fundo de troco');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cash/open`, {
        operator_id: openForm.operator_id,
        initial_amount: parseFloat(openForm.initial_amount),
        notes: openForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Caixa aberto com sucesso!');
      setShowOpenModal(false);
      setOpenForm({ operator_id: '', initial_amount: '', notes: '' });
      fetchCurrentCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir caixa');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCash = async (e) => {
    e.preventDefault();
    if (!closeForm.actual_amount) {
      toast.error('Informe o valor em caixa');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/cash/close`, {
        actual_amount: parseFloat(closeForm.actual_amount),
        notes: closeForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Caixa fechado com sucesso!');
      setShowCloseModal(false);
      setCloseForm({ actual_amount: '', notes: '' });
      
      // Mostrar detalhes para impressão
      setSelectedRegister(response.data);
      setShowDetailModal(true);
      
      fetchCurrentCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fechar caixa');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMovement = async (e) => {
    e.preventDefault();
    if (!movementForm.amount || parseFloat(movementForm.amount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    if (!movementForm.reason.trim()) {
      toast.error('Informe o motivo');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cash/movement`, {
        type: movementType,
        amount: parseFloat(movementForm.amount),
        reason: movementForm.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const label = movementType === 'sangria' ? 'Sangria' : 'Reforço';
      toast.success(`${label} registrada!`);
      setShowMovementModal(false);
      setMovementForm({ amount: '', reason: '' });
      fetchCurrentCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar movimentação');
    } finally {
      setSaving(false);
    }
  };

  const handleAddOperator = async () => {
    if (!newOperator.trim()) {
      toast.error('Informe o nome do operador');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cash/operators`, {
        name: newOperator.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Operador cadastrado!');
      setNewOperator('');
      fetchOperators();
    } catch (error) {
      toast.error('Erro ao cadastrar operador');
    }
  };

  const handleDeleteOperator = async (id) => {
    if (!window.confirm('Remover este operador?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cash/operators/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Operador removido');
      fetchOperators();
    } catch (error) {
      toast.error('Erro ao remover operador');
    }
  };

  const viewRegisterDetail = async (register) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash/${register.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRegister(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Fechamento de Caixa</title>
          <style>
            body { font-family: monospace; font-size: 12px; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .line { border-top: 1px dashed #000; margin: 10px 0; }
            .row { display: flex; justify-content: space-between; margin: 5px 0; }
            .bold { font-weight: bold; }
            .center { text-align: center; }
            .right { text-align: right; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toFixed(2)}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <Wallet className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Caixa</h1>
            <p className="text-gray-400 text-sm">Controle de abertura e fechamento</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { fetchHistory(); setShowHistoryModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all"
          >
            <History className="w-4 h-4" />
            Histórico
          </button>
          <button
            onClick={() => setShowOperatorsModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all"
          >
            <Users className="w-4 h-4" />
            Operadores
          </button>
        </div>
      </div>

      {/* Status do Caixa */}
      {!isOpen ? (
        // Caixa fechado
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wallet className="w-8 h-8 text-gray-500" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Caixa Fechado</h2>
          <p className="text-gray-400 mb-6">Abra o caixa para iniciar as operações do dia</p>
          
          {operators.length === 0 ? (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
              <p className="text-yellow-400 text-sm">
                Cadastre pelo menos um operador antes de abrir o caixa
              </p>
              <button
                onClick={() => setShowOperatorsModal(true)}
                className="mt-2 text-yellow-400 underline text-sm"
              >
                Cadastrar operador
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowOpenModal(true)}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all flex items-center gap-2 mx-auto"
            >
              <DollarSign className="w-5 h-5" />
              Abrir Caixa
            </button>
          )}
        </div>
      ) : (
        // Caixa aberto
        <div className="space-y-6">
          {/* Card Principal */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-semibold">CAIXA ABERTO</span>
              </div>
              <div className="text-gray-400 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Desde {formatDateTime(currentCash?.cash_register?.opened_at)}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">Operador: </span>
              <span className="text-white font-medium">{currentCash?.operator_name}</span>
            </div>

            {/* Resumo - Vendas por Forma de Pagamento */}
            <div className="mb-6">
              <h3 className="text-gray-300 text-sm font-medium mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Vendas por Forma de Pagamento
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {/* Dinheiro */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <p className="text-gray-400 text-xs">Dinheiro</p>
                  </div>
                  <p className="text-green-400 text-xl font-bold">{formatCurrency(currentCash?.summary?.sales_by_payment?.dinheiro?.total || 0)}</p>
                  <p className="text-gray-500 text-xs">{currentCash?.summary?.sales_by_payment?.dinheiro?.count || 0} pedidos</p>
                </div>
                {/* PIX */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-purple-400" />
                    <p className="text-gray-400 text-xs">PIX</p>
                  </div>
                  <p className="text-purple-400 text-xl font-bold">{formatCurrency(currentCash?.summary?.sales_by_payment?.pix?.total || 0)}</p>
                  <p className="text-gray-500 text-xs">{currentCash?.summary?.sales_by_payment?.pix?.count || 0} pedidos</p>
                </div>
                {/* Cartão Crédito */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-emerald-400" />
                    <p className="text-gray-400 text-xs">Crédito</p>
                  </div>
                  <p className="text-emerald-400 text-xl font-bold">{formatCurrency(currentCash?.summary?.sales_by_payment?.credito?.total || currentCash?.summary?.sales_by_payment?.cartao_credito?.total || 0)}</p>
                  <p className="text-gray-500 text-xs">{currentCash?.summary?.sales_by_payment?.credito?.count || currentCash?.summary?.sales_by_payment?.cartao_credito?.count || 0} pedidos</p>
                </div>
                {/* Cartão Débito */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-cyan-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-cyan-400" />
                    <p className="text-gray-400 text-xs">Débito</p>
                  </div>
                  <p className="text-cyan-400 text-xl font-bold">{formatCurrency(currentCash?.summary?.sales_by_payment?.debito?.total || currentCash?.summary?.sales_by_payment?.cartao_debito?.total || 0)}</p>
                  <p className="text-gray-500 text-xs">{currentCash?.summary?.sales_by_payment?.debito?.count || currentCash?.summary?.sales_by_payment?.cartao_debito?.count || 0} pedidos</p>
                </div>
                {/* Cartão (Genérico) */}
                {currentCash?.summary?.sales_by_payment?.cartao && (
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-indigo-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="w-4 h-4 text-indigo-400" />
                      <p className="text-gray-400 text-xs">Cartão</p>
                    </div>
                    <p className="text-indigo-400 text-xl font-bold">{formatCurrency(currentCash?.summary?.sales_by_payment?.cartao?.total || 0)}</p>
                    <p className="text-gray-500 text-xs">{currentCash?.summary?.sales_by_payment?.cartao?.count || 0} pedidos</p>
                  </div>
                )}
              </div>
              
              {/* Total Geral de Vendas */}
              <div className="mt-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300 font-medium">Total de Vendas (todas formas)</span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(currentCash?.summary?.total_all_sales || 0)}</p>
                    <p className="text-gray-500 text-xs">{currentCash?.summary?.total_all_count || 0} pedidos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Movimentações de Caixa */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-xs mb-1">Fundo Inicial</p>
                <p className="text-white text-xl font-bold">{formatCurrency(currentCash?.summary?.initial_amount)}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-xs mb-1">Vendas Dinheiro</p>
                <p className="text-green-400 text-xl font-bold">+{formatCurrency(currentCash?.summary?.cash_sales)}</p>
                <p className="text-gray-500 text-xs">{currentCash?.summary?.cash_sales_count || 0} pedidos</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-xs mb-1">Sangrias</p>
                <p className="text-red-400 text-xl font-bold">-{formatCurrency(currentCash?.summary?.total_sangrias)}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-xs mb-1">Reforços</p>
                <p className="text-emerald-400 text-xl font-bold">+{formatCurrency(currentCash?.summary?.total_reforcos)}</p>
              </div>
            </div>

            {/* Valor Esperado */}
            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Valor esperado em caixa:</span>
                <span className="text-2xl font-bold text-white">{formatCurrency(currentCash?.summary?.expected_amount)}</span>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => { setMovementType('sangria'); setShowMovementModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-400 font-medium rounded-lg transition-all"
              >
                <Minus className="w-4 h-4" />
                Sangria
              </button>
              <button
                onClick={() => { setMovementType('reforco'); setShowMovementModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 font-medium rounded-lg transition-all"
              >
                <Plus className="w-4 h-4" />
                Reforço
              </button>
              <button
                onClick={() => setShowCloseModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-all ml-auto"
              >
                <FileText className="w-4 h-4" />
                Fechar Caixa
              </button>
            </div>
          </div>

          {/* Movimentações do Dia */}
          {currentCash?.movements?.length > 0 && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Movimentações do Dia</h3>
              <div className="space-y-2">
                {currentCash.movements.map((mov) => (
                  <div key={mov.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                    <div className="flex items-center gap-3">
                      {mov.type === 'sangria' ? (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      ) : (
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      )}
                      <div>
                        <p className="text-white text-sm">{mov.reason}</p>
                        <p className="text-gray-500 text-xs">{formatDateTime(mov.created_at)}</p>
                      </div>
                    </div>
                    <span className={`font-medium ${mov.type === 'sangria' ? 'text-red-400' : 'text-emerald-400'}`}>
                      {mov.type === 'sangria' ? '-' : '+'}{formatCurrency(mov.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Abrir Caixa */}
      {showOpenModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Abrir Caixa</h3>
              <button onClick={() => setShowOpenModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleOpenCash} className="p-4 space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Operador *</label>
                <select
                  value={openForm.operator_id}
                  onChange={(e) => setOpenForm({ ...openForm, operator_id: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="">Selecione o operador</option>
                  {operators.filter(op => op.active !== false).map(op => (
                    <option key={op.id} value={op.id}>{op.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Fundo de Troco (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={openForm.initial_amount}
                  onChange={(e) => setOpenForm({ ...openForm, initial_amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Observações</label>
                <textarea
                  value={openForm.notes}
                  onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })}
                  placeholder="Opcional"
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowOpenModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Abrindo...' : 'Abrir Caixa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Fechar Caixa */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Fechar Caixa</h3>
              <button onClick={() => setShowCloseModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCloseCash} className="p-4 space-y-4">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Valor esperado em caixa:</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(currentCash?.summary?.expected_amount)}</p>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Valor Real em Caixa (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={closeForm.actual_amount}
                  onChange={(e) => setCloseForm({ ...closeForm, actual_amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg"
                />
              </div>
              {closeForm.actual_amount && (
                <div className={`rounded-lg p-3 ${
                  parseFloat(closeForm.actual_amount) - (currentCash?.summary?.expected_amount || 0) >= 0 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <p className="text-sm text-gray-300">Diferença:</p>
                  <p className={`text-xl font-bold ${
                    parseFloat(closeForm.actual_amount) - (currentCash?.summary?.expected_amount || 0) >= 0 
                      ? 'text-green-400' 
                      : 'text-red-400'
                  }`}>
                    {formatCurrency(parseFloat(closeForm.actual_amount) - (currentCash?.summary?.expected_amount || 0))}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Observações</label>
                <textarea
                  value={closeForm.notes}
                  onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                  placeholder="Opcional"
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCloseModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Fechando...' : 'Fechar Caixa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Sangria/Reforço */}
      {showMovementModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">
                {movementType === 'sangria' ? 'Registrar Sangria' : 'Registrar Reforço'}
              </h3>
              <button onClick={() => setShowMovementModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddMovement} className="p-4 space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Valor (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={movementForm.amount}
                  onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Motivo *</label>
                <input
                  type="text"
                  value={movementForm.reason}
                  onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
                  placeholder={movementType === 'sangria' ? 'Ex: Pagamento fornecedor' : 'Ex: Troco adicional'}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowMovementModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    movementType === 'sangria' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {saving ? 'Registrando...' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Histórico */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Histórico de Caixas (30 dias)</h3>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {history.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nenhum registro encontrado</p>
              ) : (
                <div className="space-y-3">
                  {history.map((reg) => (
                    <div 
                      key={reg.id} 
                      className="bg-gray-700/50 rounded-lg p-4 hover:bg-gray-700 cursor-pointer transition-all"
                      onClick={() => viewRegisterDetail(reg)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{formatDate(reg.opened_at)}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          reg.status === 'aberto' ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-300'
                        }`}>
                          {reg.status === 'aberto' ? 'Aberto' : 'Fechado'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Operador: {reg.operator_name}</span>
                        {reg.status === 'fechado' && (
                          <span className={`font-medium ${
                            (reg.difference || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            Diferença: {formatCurrency(reg.difference)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Operadores */}
      {showOperatorsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Operadores de Caixa</h3>
              <button onClick={() => setShowOperatorsModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {/* Adicionar novo */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newOperator}
                  onChange={(e) => setNewOperator(e.target.value)}
                  placeholder="Nome do operador"
                  className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddOperator()}
                />
                <button
                  onClick={handleAddOperator}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              {/* Lista */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {operators.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Nenhum operador cadastrado</p>
                ) : (
                  operators.map((op) => (
                    <div key={op.id} className="flex items-center justify-between py-2 px-3 bg-gray-700/50 rounded-lg">
                      <span className="text-white">{op.name}</span>
                      <button
                        onClick={() => handleDeleteOperator(op.id)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalhes / Impressão */}
      {showDetailModal && selectedRegister && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg border border-gray-700 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Fechamento de Caixa</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrint}
                  className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  <Printer className="w-5 h-5" />
                </button>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {/* Conteúdo para impressão */}
              <div id="print-content" className="bg-white text-black p-4 rounded-lg">
                <div className="header">
                  <h2 className="bold">{selectedRegister.restaurant_name || 'Restaurante'}</h2>
                  <p>FECHAMENTO DE CAIXA</p>
                  <p>{formatDate(selectedRegister.cash_register?.closed_at || selectedRegister.cash_register?.opened_at)}</p>
                </div>
                <div className="line"></div>
                
                <div className="row">
                  <span>Operador:</span>
                  <span>{selectedRegister.cash_register?.operator_name}</span>
                </div>
                <div className="row">
                  <span>Abertura:</span>
                  <span>{formatDateTime(selectedRegister.cash_register?.opened_at)}</span>
                </div>
                {selectedRegister.cash_register?.closed_at && (
                  <div className="row">
                    <span>Fechamento:</span>
                    <span>{formatDateTime(selectedRegister.cash_register?.closed_at)}</span>
                  </div>
                )}
                
                <div className="line"></div>
                
                <div className="row">
                  <span>Fundo Inicial:</span>
                  <span>{formatCurrency(selectedRegister.cash_register?.initial_amount)}</span>
                </div>
                
                <div className="line"></div>
                <p className="bold center">VENDAS POR FORMA DE PAGAMENTO</p>
                
                {selectedRegister.cash_register?.summary?.sales_by_payment && 
                  Object.entries(selectedRegister.cash_register.summary.sales_by_payment).map(([method, data]) => (
                    <div key={method} className="row">
                      <span>{method.charAt(0).toUpperCase() + method.slice(1).replace('_', ' ')} ({data.count}x):</span>
                      <span>{formatCurrency(data.total)}</span>
                    </div>
                  ))
                }
                
                <div className="line"></div>
                
                <div className="row">
                  <span>Vendas Dinheiro:</span>
                  <span>+{formatCurrency(selectedRegister.cash_register?.summary?.cash_sales)}</span>
                </div>
                <div className="row">
                  <span>Sangrias:</span>
                  <span>-{formatCurrency(selectedRegister.cash_register?.summary?.total_sangrias)}</span>
                </div>
                <div className="row">
                  <span>Reforços:</span>
                  <span>+{formatCurrency(selectedRegister.cash_register?.summary?.total_reforcos)}</span>
                </div>
                
                <div className="line"></div>
                
                <div className="row bold">
                  <span>ESPERADO:</span>
                  <span>{formatCurrency(selectedRegister.cash_register?.expected_amount)}</span>
                </div>
                <div className="row bold">
                  <span>INFORMADO:</span>
                  <span>{formatCurrency(selectedRegister.cash_register?.final_amount)}</span>
                </div>
                <div className="row bold">
                  <span>DIFERENÇA:</span>
                  <span>{formatCurrency(selectedRegister.cash_register?.difference)}</span>
                </div>
                
                {selectedRegister.movements?.length > 0 && (
                  <>
                    <div className="line"></div>
                    <p className="bold center">MOVIMENTAÇÕES</p>
                    {selectedRegister.movements.map((mov, idx) => (
                      <div key={idx} className="row" style={{ fontSize: '10px' }}>
                        <span>{mov.type === 'sangria' ? '[-]' : '[+]'} {mov.reason}</span>
                        <span>{formatCurrency(mov.amount)}</span>
                      </div>
                    ))}
                  </>
                )}
                
                <div className="line"></div>
                <p className="center" style={{ fontSize: '10px', marginTop: '10px' }}>
                  Documento gerado em {formatDateTime(new Date().toISOString())}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRegisterContent;
